/**
 * Desktop client tools: the agent can ask to use the creator's computer (look at
 * their screen, open a link in their browser, read their clipboard), and the
 * creator answers on a card in the conversation (DesktopCallCard).
 *
 * Every message this window sends carries `client` (`desktopClientInfo`): what
 * the app can do, and a `client_id` that names this window for as long as the
 * page lives (sessionStorage, so a reload keeps it). The backend only offers the
 * `desktop` tool to turns that came with one, and only this window gets the
 * buttons for the asks those turns make. Outside the desktop app there is no
 * client info and nothing here does anything.
 */
import type { ClientInfo } from '@/agent/api'
import type { ClientCallAction } from '@/agent/protocol/types'
import { getDesktopBridge } from './bridge'
import type { LighthouseDesktopBridge } from './types'

const CLIENT_ID_KEY = 'lighthouse.desktop.clientId'

/** Without sessionStorage (blocked, tests): one id for the page's life. */
let memoryId: string | null = null

function newClientId(): string {
    const uuid = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0
            return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
        })
    return `desktop-${uuid}`
}

function storedId(): string | null {
    try {
        const v = sessionStorage.getItem(CLIENT_ID_KEY)
        return v && v.startsWith('desktop-') ? v : null
    } catch {
        return null
    }
}

/** This window's client id (`desktop-<uuid>`), created on first use. Null outside the desktop app. */
export function desktopClientId(): string | null {
    if (!getDesktopBridge()) return null
    const existing = storedId() ?? memoryId
    if (existing) return existing
    const id = newClientId()
    memoryId = id
    try {
        sessionStorage.setItem(CLIENT_ID_KEY, id)
    } catch {
        /* the in-memory id stands in */
    }
    return id
}

/** What this desktop app lets the agent ask for. */
export function desktopCapabilities(bridge: LighthouseDesktopBridge): ClientCallAction[] {
    const caps: ClientCallAction[] = []
    if (typeof bridge.capture?.screenshot === 'function') caps.push('capture_screen')
    if (typeof bridge.files?.openExternal === 'function') caps.push('open_url')
    caps.push('read_clipboard')
    return caps
}

/** The `client` a message sends from the desktop app; null everywhere else. */
export function desktopClientInfo(): ClientInfo | null {
    const bridge = getDesktopBridge()
    const clientId = desktopClientId()
    if (!bridge || !clientId) return null
    return {
        kind: 'desktop',
        client_id: clientId,
        platform: bridge.platform ?? null,
        app_version: bridge.version ?? null,
        capabilities: desktopCapabilities(bridge),
    }
}

/** `body` plus `client` in the desktop app; `body` unchanged on the web and on phones. */
export function withDesktopClient<T extends object>(body: T): T & { client?: ClientInfo } {
    const client = desktopClientInfo()
    return client ? { ...body, client } : body
}

/** `brand.com` from a link (without `www.`), or the link itself when it doesn't parse. */
export function linkHost(url: string | null | undefined): string {
    if (!url) return 'the link'
    try {
        return new URL(url).hostname.replace(/^www\./, '') || url
    } catch {
        return url
    }
}

/** The ask, as the card's heading and the notification's title. */
export function clientCallTitle(action: ClientCallAction, url?: string | null): string {
    if (action === 'capture_screen') return 'Lighthouse wants to look at your screen'
    if (action === 'read_clipboard') return 'Lighthouse wants to read your clipboard'
    return `Open ${linkHost(url)} in your browser?`
}

/** Tests: forget the in-memory id. */
export function resetDesktopClientId(): void {
    memoryId = null
    try {
        sessionStorage.removeItem(CLIENT_ID_KEY)
    } catch {
        /* nothing stored */
    }
}
